/* ═══════════════════════════════════════════════════════════════
   ProjectToolHost — Project Canvas R1 (T3). Renders the REAL sidebar page
   component nested inside a project's takeover area, scoped to this
   project (Decision 5/6: nothing navigates away from a project; the
   nested surface IS the sidebar page, never a copy or a subset).

   One path for every widget "expand" and every tool-opening drill —
   Dashboard.tsx's `handleDrill`/wrapped `navigate` route here instead of
   calling the app-shell `navigate`. Each page already accepts, additively,
   a `projectId` + `embedded` prop (presets the project filter without
   locking it — Decision 7) and a scoped `deepLink`.
   ═══════════════════════════════════════════════════════════════ */
import React from 'react';
import { TasksKit } from '../tasks/TasksKit';
import InvoicesPageNew from '../InvoicesPageNew';
import ForecastingPage2 from '../forecasting2';
import TimeExpensePage from '../time/TimeExpensePage';
import type { AuthUser } from '../../services/authService';
import type { DeepLink } from '../../types/navigation';
import type { GatedTool } from '../../lib/pageGates';

/** Tool kinds Project Canvas nests — 'team' is a `GatedTool` for the
 *  shared gate predicate but is never nested (Decision 14 exception). */
export type ProjectToolKind = Exclude<GatedTool, 'team'>;

export interface ProjectToolHostProps {
  tool: ProjectToolKind;
  projectId: string;
  deepLink?: DeepLink | null;
  authUser?: AuthUser | null;
  toast: (msg: string, isError?: boolean) => void;
}

export function ProjectToolHost({
  tool,
  projectId,
  deepLink,
  authUser,
  toast,
}: ProjectToolHostProps) {
  switch (tool) {
    case 'tasks':
      return (
        <TasksKit
          toast={toast}
          authUser={authUser}
          scope={{ projectId }}
          deepLink={deepLink && deepLink.view === 'tasks' ? deepLink : null}
        />
      );
    case 'invoices':
      return (
        <InvoicesPageNew
          toast={toast}
          authUser={authUser}
          projectId={projectId}
          embedded
          deepLink={deepLink && deepLink.view === 'invoices' ? deepLink : null}
        />
      );
    case 'forecasting':
      return (
        <ForecastingPage2
          toast={toast}
          authUser={authUser}
          projectId={projectId}
          embedded
          deepLink={deepLink && deepLink.view === 'forecasting' ? deepLink : null}
        />
      );
    case 'time-expense':
      // Time & Expense has no `projectId` prop of its own (Decision 14 —
      // not rebuilt here); the project filter rides the deep link, which
      // its Timesheets tab already consumes.
      return (
        <TimeExpensePage
          toast={toast}
          authUser={authUser}
          embedded
          deepLink={deepLink && deepLink.view === 'time-expense' ? deepLink : null}
        />
      );
    default:
      return null;
  }
}
